/**
 * steps.ts
 *
 * Pure helpers describing the Title Search (TR) workflow's four ordered steps
 * and computing the "current" step + status for a TR job so the job list view
 * can show where each job is (Requirement 8.4) and the consolidated detail page
 * can drive step progress (Requirement 8.1).
 *
 * The four steps differ from the DD/BA three-step model, so they are modeled
 * here rather than overloading the shared `StepType`/`getCurrentStep` logic.
 *
 * This module is pure (no React, no I/O) so it stays trivially testable.
 *
 * Requirements:
 *   8.1 - ordered steps: Property Tax Search, Recorded Document Search,
 *         Document Processing, Findings Display.
 *   8.4 - the job list view shows the current step and status for each TR job.
 */

import type { TitleSearchStepStatus } from './polling';

/** The four ordered Title Search steps (Requirement 8.1). */
export type TitleSearchStep =
  | 'property_tax_search'
  | 'recorded_document_search'
  | 'document_processing'
  | 'findings_display';

/** The steps in their defined workflow order (Requirement 8.1). */
export const TITLE_SEARCH_STEP_ORDER: readonly TitleSearchStep[] = [
  'property_tax_search',
  'recorded_document_search',
  'document_processing',
  'findings_display',
];

/**
 * The minimal slice of a job's step-status fields this module needs. Field
 * names mirror the GraphQL `Job` type. `documentProcessingStatus` reuses the
 * existing DD/BA `StepStatus` enum (e.g. `COMPLETED`), while the other three
 * use the title-search status domain; both are accepted as raw strings so the
 * normalization below is the single place that reconciles casing.
 */
export interface TitleSearchStepStatusFields {
  propertyTaxSearchStatus?: string | null;
  recordedDocumentSearchStatus?: string | null;
  documentProcessingStatus?: string | null;
  findingsDisplayStatus?: string | null;
}

/**
 * Statuses that mean a step has advanced past (the workflow may move on):
 * `completed` and `not_found` both allow the next step to run (Requirements
 * 2.6, 3.1). Comparison is case-insensitive so the DD/BA `COMPLETED` and the
 * title-search `completed` both count.
 */
const ADVANCED_STATUSES: ReadonlySet<string> = new Set([
  'completed',
  'not_found',
]);

/** Normalize any status value to the lower-case, trimmed comparison form. */
function normalizeStatus(status: string | null | undefined): string {
  return (status ?? '').trim().toLowerCase();
}

/** Read the raw status string for a given step off the job's fields. */
export function getStepStatus(
  job: TitleSearchStepStatusFields,
  step: TitleSearchStep
): TitleSearchStepStatus {
  let raw: string | null | undefined;
  switch (step) {
    case 'property_tax_search':
      raw = job.propertyTaxSearchStatus;
      break;
    case 'recorded_document_search':
      raw = job.recordedDocumentSearchStatus;
      break;
    case 'document_processing':
      raw = job.documentProcessingStatus;
      break;
    case 'findings_display':
      raw = job.findingsDisplayStatus;
      break;
  }
  const normalized = normalizeStatus(raw);
  // Map the DD/BA StepStatus spellings onto the title-search domain so callers
  // get a consistent value regardless of which step they asked about.
  switch (normalized) {
    case 'in_progress':
      return 'in_progress';
    case 'completed':
      return 'completed';
    case 'not_found':
      return 'not_found';
    case 'failed':
      return 'failed';
    case 'blocked':
      return 'blocked';
    default:
      // NOT_STARTED, empty, or anything unexpected → not yet started.
      return 'pending';
  }
}

/**
 * Determine the current (active) Title Search step for a job: the first step in
 * the defined order whose status has not advanced (i.e. is not `completed` or
 * `not_found`). When every step has advanced, the last step (Findings Display)
 * is considered current.
 *
 * Requirements: 8.4
 */
export function getCurrentTitleSearchStep(
  job: TitleSearchStepStatusFields
): TitleSearchStep {
  for (const step of TITLE_SEARCH_STEP_ORDER) {
    const status = normalizeStatus(getStepStatus(job, step));
    if (!ADVANCED_STATUSES.has(status)) {
      return step;
    }
  }
  return TITLE_SEARCH_STEP_ORDER[TITLE_SEARCH_STEP_ORDER.length - 1];
}

/**
 * Convenience: the current step paired with its status, for the list view's
 * "current step / status" display (Requirement 8.4).
 */
export function getCurrentTitleSearchStepWithStatus(
  job: TitleSearchStepStatusFields
): { step: TitleSearchStep; status: TitleSearchStepStatus } {
  const step = getCurrentTitleSearchStep(job);
  return { step, status: getStepStatus(job, step) };
}

/**
 * Human-readable label for a status value, suitable for direct display
 * (e.g. `in_progress` → "In Progress", `not_found` → "Not Found").
 */
export function formatStepStatusLabel(
  status: TitleSearchStepStatus | string | null | undefined
): string {
  const normalized = normalizeStatus(status);
  if (!normalized) {
    return 'Pending';
  }
  return normalized
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
